import { Plus, Minus } from "lucide-react";
import { Reveal } from "./Reveal";

export type FaqItem = { q: string; a: string };

export function FaqSection({
  eyebrow = "VANLIGA FRÅGOR",
  heading,
  intro,
  items,
}: {
  eyebrow?: string;
  heading: string;
  intro?: string;
  items: FaqItem[];
}) {
  return (
    <section className="py-20 md:py-28 border-t border-border bg-card/30">
      <div className="mx-auto max-w-4xl px-5 md:px-10">
        <Reveal>
          <p className="section-number">{eyebrow}</p>
          <h2 className="mt-4 text-3xl md:text-5xl">{heading}</h2>
          {intro && (
            <p className="mt-5 text-muted-foreground font-light leading-relaxed max-w-2xl">
              {intro}
            </p>
          )}
        </Reveal>
        <div className="mt-12 divide-y divide-border border-y border-border">
          {items.map((it, i) => (
            <details key={it.q} className="group" open={i === 0 || undefined}>
              <summary className="flex w-full cursor-pointer list-none items-start justify-between gap-6 py-6 text-left [&::-webkit-details-marker]:hidden">
                <h3 className="text-lg md:text-xl font-display text-foreground group-hover:text-gold transition-colors">
                  {it.q}
                </h3>
                <span className="mt-1 shrink-0 flex h-7 w-7 items-center justify-center rounded-full border border-border text-gold">
                  <Plus size={14} className="group-open:hidden" aria-hidden="true" />
                  <Minus size={14} className="hidden group-open:block" aria-hidden="true" />
                </span>
              </summary>
              <div className="faq-answer pb-6 pr-12 text-muted-foreground leading-relaxed font-light whitespace-pre-line">
                {it.a}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export function faqJsonLd(items: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}
